import React, { useEffect } from "react";
import {
  FaChartPie,
  FaTimes,
  FaRegSmile,
  FaRegFrown,
  FaRegMeh,
  FaLightbulb,
  FaUserCircle,
  FaBolt,
  FaCheckCircle,
  FaBullseye,
} from "react-icons/fa";

const ENGAGEMENT_TONE = {
  High: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  Medium: "bg-amber-50 text-amber-700 ring-amber-100",
  Low: "bg-rose-50 text-rose-700 ring-rose-100",
};

/**
 * CommentAnalysisModal
 * ------------------------------------------------------------------
 * Read-only presentation of an AI comment analysis result.
 * Does NOT trigger any network call – the parent fetches analysis
 * on an explicit click and passes the result in.
 *
 * Layout notes:
 * - z-[130] sits above top navbar (z-50) and bottom navbar (z-[120])
 * - Overlay padding keeps the panel clear of both navbars
 * - Header is fixed; body scrolls independently
 */
const CommentAnalysisModal = ({ automation, result, onClose }) => {
  const analysis = result?.analysis || {};
  const meta = result?.meta || {};
  const cached = Boolean(result?.cached);

  const positive = analysis.positiveCount || 0;
  const negative = analysis.negativeCount || 0;
  const neutral = analysis.neutralCount || 0;
  const total = positive + negative + neutral || 1;
  const pct = (n) => Math.round((n / total) * 100);

  const score = analysis.sentimentScore ?? 0;
  const engagement = analysis.engagementQuality || "Low";
  const analyzedAt = meta.analyzedAt
    ? new Date(meta.analyzedAt).toLocaleString()
    : null;

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 px-3 pt-[calc(70px+0.5rem)] pb-[calc(4.75rem+env(safe-area-inset-bottom,0px))] sm:px-4 lg:pb-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="comment-analysis-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="flex min-h-0 w-full max-w-lg max-h-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky header */}
        <div className="flex shrink-0 items-start justify-between gap-2 border-b border-gray-100 bg-white px-3 py-2.5 sm:px-4 sm:py-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <FaChartPie className="h-4 w-4 shrink-0 text-orange-600" />
              <h2
                id="comment-analysis-title"
                className="text-sm font-bold text-gray-900 sm:text-base"
              >
                Comment Analysis
              </h2>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                  cached
                    ? "bg-gray-100 text-gray-600"
                    : "bg-emerald-50 text-emerald-700"
                }`}
              >
                {cached ? "Cached" : "Fresh"}
              </span>
            </div>
            <p className="mt-1 truncate text-xs text-gray-500">
              {automation?.uploadPost?.title || "Untitled post"}
              {meta.analyzedCommentCount != null && (
                <> · {meta.analyzedCommentCount} comments analyzed</>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            title="Close"
            aria-label="Close"
          >
            <FaTimes className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto overscroll-contain px-3 py-2.5 sm:space-y-3 sm:px-4 sm:py-3">
          {/* Compact metrics strip */}
          <div className="grid grid-cols-3 gap-1 sm:gap-1.5">
            <div className="flex flex-col items-center justify-center rounded-lg border border-orange-100 bg-gradient-to-br from-orange-50 to-amber-50 px-1 py-1.5 sm:px-1.5 sm:py-2">
              <p className="text-[8px] font-semibold uppercase tracking-wide text-orange-600 sm:text-[9px]">
                Score
              </p>
              <p className="mt-0.5 text-base font-extrabold leading-none text-gray-900 sm:text-lg">
                {score}
              </p>
              <p className="mt-0.5 text-[8px] text-gray-500 sm:text-[9px]">/ 100</p>
            </div>
            <div className="flex flex-col items-center justify-center rounded-lg border border-gray-100 bg-gray-50 px-1 py-1.5 sm:px-1.5 sm:py-2">
              <p className="text-[8px] font-semibold uppercase tracking-wide text-gray-500 sm:text-[9px]">
                Engage
              </p>
              <span
                className={`mt-0.5 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold ring-1 ring-inset sm:text-[10px] ${
                  ENGAGEMENT_TONE[engagement] || ENGAGEMENT_TONE.Low
                }`}
              >
                <FaBolt className="h-2 w-2" />
                {engagement}
              </span>
            </div>
            <div className="flex flex-col items-center justify-center rounded-lg border border-gray-100 bg-gray-50 px-1 py-1.5 sm:px-1.5 sm:py-2">
              <p className="text-[8px] font-semibold uppercase tracking-wide text-gray-500 sm:text-[9px]">
                Comments
              </p>
              <p className="mt-0.5 text-base font-bold leading-none text-gray-900 sm:text-lg">
                {total}
              </p>
            </div>
          </div>

          {/* Sentiment breakdown */}
          <div>
            <div className="grid grid-cols-3 gap-1 sm:gap-1.5">
              <SentimentCard
                label="Positive"
                value={positive}
                pct={pct(positive)}
                tone="positive"
                Icon={FaRegSmile}
              />
              <SentimentCard
                label="Neutral"
                value={neutral}
                pct={pct(neutral)}
                tone="neutral"
                Icon={FaRegMeh}
              />
              <SentimentCard
                label="Negative"
                value={negative}
                pct={pct(negative)}
                tone="negative"
                Icon={FaRegFrown}
              />
            </div>
            <div className="mt-1.5 flex h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
              <div
                className="bg-emerald-500"
                style={{ width: `${pct(positive)}%` }}
              />
              <div
                className="bg-gray-300"
                style={{ width: `${pct(neutral)}%` }}
              />
              <div
                className="bg-rose-500"
                style={{ width: `${pct(negative)}%` }}
              />
            </div>
          </div>

          {analysis.summary && (
            <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-2 sm:p-2.5">
              <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-blue-700 sm:text-[10px]">
                <FaCheckCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                Summary
              </div>
              <p className="mt-1 text-[11px] leading-snug text-gray-700 sm:text-xs">
                {analysis.summary}
              </p>
            </div>
          )}

          {analysis.persona && (
            <div className="rounded-lg border border-orange-100 bg-orange-50/50 p-2 sm:p-2.5">
              <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-orange-700 sm:text-[10px]">
                <FaUserCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                Audience Persona
              </div>
              <p className="mt-1 text-[11px] leading-snug text-gray-700 sm:text-xs">
                {analysis.persona}
              </p>
            </div>
          )}

          {Array.isArray(analysis.topThemes) && analysis.topThemes.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 sm:text-[11px]">
                <FaBullseye className="h-3 w-3 text-purple-500 sm:h-3.5 sm:w-3.5" />
                Top Themes
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {analysis.topThemes.map((theme, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center rounded-full bg-purple-50 px-2.5 py-0.5 text-[11px] font-medium text-purple-700 ring-1 ring-inset ring-purple-100 sm:px-3 sm:py-1 sm:text-xs"
                  >
                    {theme}
                  </span>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(analysis.insights) && analysis.insights.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 sm:text-[11px]">
                <FaLightbulb className="h-3 w-3 text-amber-500 sm:h-3.5 sm:w-3.5" />
                Key Insights
              </div>
              <ul className="mt-1 space-y-1">
                {analysis.insights.map((insight, idx) => (
                  <li
                    key={idx}
                    className="flex gap-1.5 rounded-lg border border-gray-100 bg-gray-50 p-2 text-[11px] text-gray-700 sm:text-xs"
                  >
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-100 text-[9px] font-bold text-amber-700 sm:h-5 sm:w-5 sm:text-[10px]">
                      {idx + 1}
                    </span>
                    <span className="leading-relaxed">{insight}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {Array.isArray(analysis.recommendedActions) &&
            analysis.recommendedActions.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 sm:text-[11px]">
                  <FaBolt className="h-3 w-3 text-emerald-500 sm:h-3.5 sm:w-3.5" />
                  Recommended Actions
                </div>
                <ul className="mt-1 space-y-1">
                  {analysis.recommendedActions.map((action, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-1.5 rounded-lg border border-emerald-100 bg-emerald-50/60 p-2 text-[11px] text-gray-700 sm:text-xs"
                    >
                      <FaCheckCircle className="mt-0.5 h-3 w-3 shrink-0 text-emerald-500" />
                      <span className="leading-relaxed">{action}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

          {analyzedAt && (
            <p className="pb-1 text-right text-[10px] text-gray-400">
              Last analyzed: {analyzedAt}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const SentimentCard = ({ label, value, pct, tone, Icon }) => {
  const toneStyles = {
    positive: "border-emerald-100 bg-emerald-50 text-emerald-700",
    neutral: "border-gray-100 bg-gray-50 text-gray-600",
    negative: "border-rose-100 bg-rose-50 text-rose-700",
  };
  return (
    <div className={`rounded-md border px-1 py-1.5 text-center sm:rounded-lg sm:px-1.5 sm:py-2 ${toneStyles[tone]}`}>
      <Icon className="mx-auto h-3 w-3 sm:h-3.5 sm:w-3.5" />
      <p className="mt-0.5 text-sm font-bold leading-none sm:text-base">{value}</p>
      <p className="mt-0.5 text-[8px] font-medium uppercase tracking-wide sm:text-[9px]">
        {label}
      </p>
      <p className="text-[8px] opacity-70 sm:text-[9px]">{pct}%</p>
    </div>
  );
};

export default CommentAnalysisModal;
